import { Avatar, Col, Form, FormInstance, Input, Row, Select } from 'antd'
import {
  formValidationOnRequiredFields,
  getNameSignature,
} from 'helper/utility'
import React from 'react'
import { leagueEventStore } from 'state/league/leagueEvent'
import { manageTeamStore } from 'state/league/manageTeam'
import { LeagueFinalResp } from 'types/league/league'
import TeamSelect from './Inputs/TeamSelect'
import DivisionSelect from './Inputs/DivisionSelect'
import { LeagueTeamPayload } from 'types/team/leagueTeam'
import { myStore } from 'state/store'
import {
  useCreateTeamForLeague,
  useUpdateTeamInOrganization,
} from 'api/Team/LeagueTeam'

interface AddTeamFormProps {
  form: FormInstance
}

const AddTeamForm: React.FC<AddTeamFormProps> = ({ form }) => {
  const { useWatch } = Form
  const { Option } = Select
  const { leagueData }: { leagueData: LeagueFinalResp } = leagueEventStore(
    store => ({
      leagueData: store.leagueData,
    }),
  )
  const { setManageTeamModal, setAddTeamModal, selectedTeam, setSelectedTeam } =
    manageTeamStore(store => ({
      setManageTeamModal: store.setManageTeamModal,
      setAddTeamModal: store.setAddTeamModal,
      selectedTeam: store.selectedTeam,
      setSelectedTeam: store.setSelectedTeam,
    }))
  const setModalBtnLoader = myStore(store => store.setModalBtnLoader)

  const { mutate: createTeamForLeague } = useCreateTeamForLeague()
  const { mutate: updateTeamInLeague } = useUpdateTeamInOrganization()

  const isDirectCreated = !!selectedTeam?.isDirectCreated

  const organizations = [
    leagueData.organization,
    ...leagueData.guestOrganizations,
  ]

  const selectedOrganization = useWatch('organizationId', form)

  const formSchema = {
    organizationId: selectedTeam ? selectedTeam.organizationId : undefined,
    teamId: selectedTeam ? selectedTeam.teamId : undefined,
    divisionId: selectedTeam ? selectedTeam.divisionId : undefined,
    price: selectedTeam ? selectedTeam.price : undefined,
    minPlayers: selectedTeam ? selectedTeam.minPlayers : undefined,
    maxPlayers: selectedTeam ? selectedTeam.maxPlayers : undefined,
  }

  const handleFinish = (values: Omit<LeagueTeamPayload, 'leagueId'>) => {
    const requiredFields = isDirectCreated
      ? ['teamId', 'divisionId', 'price', 'minPlayers', 'maxPlayers']
      : ['organizationId', 'teamId', 'divisionId', 'price', 'minPlayers', 'maxPlayers']
    if (!formValidationOnRequiredFields(values, requiredFields)) return
    const payload: LeagueTeamPayload = {
      ...values,
      leagueId: leagueData._id,
    }
    setModalBtnLoader(true)

    if (selectedTeam) {
      updateTeamInLeague(
        { teamId: selectedTeam._id, payload },
        {
          onSuccess: () => {
            setSelectedTeam(null)
            setModalBtnLoader(false)
            setManageTeamModal(true)
            setAddTeamModal(false)
          },
          onError: () => {
            setModalBtnLoader(false)
          },
        },
      )
    } else {
      createTeamForLeague(
        {
          organizationId: leagueData.organizationId,
          payload,
        },
        {
          onSuccess: () => {
            setSelectedTeam(null)
            setModalBtnLoader(false)
            setManageTeamModal(true)
            setAddTeamModal(false)
          },
          onError: () => {
            setModalBtnLoader(false)
          },
        },
      )
    }
  }

  return (
    <>
      <Form
        initialValues={formSchema}
        form={form}
        layout="horizontal"
        onFinish={handleFinish}
      >
        {isDirectCreated ? (
          <>
            <Form.Item name="teamId" hidden>
              <Input />
            </Form.Item>
            <Row gutter={24} className="mb-20px">
              <Col span={24}>
                <label className="input-label">Organization</label>
                <div className="mt-10px">
                  <span
                    style={{
                      display: 'inline-block',
                      background: '#F5F5F5',
                      border: '1px solid #E0E0E0',
                      borderRadius: '8px',
                      padding: '6px 14px',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    {organizations.find(
                      org => org._id === selectedTeam?.organizationId,
                    )?.name || '—'}
                  </span>
                </div>
              </Col>
            </Row>
            <Row gutter={24} className="mb-20px">
              <Col span={24}>
                <label className="input-label">Team</label>
                <div className="mt-10px">
                  <span
                    style={{
                      display: 'inline-block',
                      background: '#F5F5F5',
                      border: '1px solid #E0E0E0',
                      borderRadius: '8px',
                      padding: '6px 14px',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    {(selectedTeam?.team as any)?.name || '—'}
                  </span>
                </div>
              </Col>
            </Row>
          </>
        ) : (
          <>
            <Row gutter={24} className="">
              <Col span={24}>
                <label htmlFor="" className="input-label required-input">
                  Select organization
                </label>
                <Form.Item
                  colon={false}
                  name="organizationId"
                  rules={[{ required: true, message: 'Organization is required' }]}
                >
                  <Select
                    placeholder="Select organization"
                    className="custom-select-field h-50px"
                  >
                    {organizations.map(org => {
                      return (
                        <Option name="kidId" value={org._id} key={org._id}>
                          <div className="flex align-center gap-16px">
                            <Avatar src={org.photo?.thumb}>
                              {getNameSignature(org.name)}
                            </Avatar>
                            <p className="text-14 w-400">{org.name}</p>
                          </div>
                        </Option>
                      )
                    })}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            {selectedOrganization && (
              <Row gutter={24} className="">
                <Col span={24}>
                  <label htmlFor="" className="input-label required-input">
                    Select Team
                  </label>
                  <Form.Item
                    colon={false}
                    name="teamId"
                    rules={[{ required: true, message: 'Team is required' }]}
                  >
                    <TeamSelect
                      form={form}
                      data={selectedTeam ? selectedTeam.teamId : undefined}
                      organizationId={selectedOrganization}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </>
        )}
        <Row gutter={24} className="">
          <Col span={24}>
            <label htmlFor="" className="input-label required-input">
              Select Division
            </label>
            <Form.Item
              colon={false}
              name="divisionId"
              rules={[{ required: true, message: 'Division is required' }]}
            >
              <DivisionSelect
                form={form}
                data={selectedTeam ? selectedTeam.division._id : undefined}
                leagueId={leagueData._id}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24} className="">
          <Col span={12}>
            <label htmlFor="" className="input-label required-input">
              Cost
            </label>
            <Form.Item
              colon={false}
              name="price"
              rules={[{ required: true, message: 'Cost is required' }]}
            >
              <Input
                type="number"
                min={0}
                placeholder="$ 0.00"
                className="br-12"
                onWheel={e => e.currentTarget.blur()}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24} className="">
          <Col span={12}>
            <label htmlFor="" className="input-label required-input">
              Min. Players
            </label>
            <Form.Item
              colon={false}
              name="minPlayers"
              rules={[
                { required: true, message: 'Min. players are required' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const max = getFieldValue('maxPlayers')
                    if (
                      value === undefined ||
                      value === null ||
                      max === undefined
                    ) {
                      return Promise.resolve()
                    }
                    if (value < max) {
                      return Promise.resolve()
                    }
                    return Promise.reject(
                      new Error('Min. players must be less than Max. players'),
                    )
                  },
                }),
              ]}
            >
              <Input
                type="number"
                min={0}
                placeholder="0"
                className="br-12"
                onWheel={e => e.currentTarget.blur()}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <label htmlFor="" className="input-label required-input">
              Max. Players
            </label>
            <Form.Item
              colon={false}
              name="maxPlayers"
              rules={[
                { required: true, message: 'Max. players are required' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const min = getFieldValue('minPlayers')
                    if (
                      value === undefined ||
                      value === null ||
                      min === undefined
                    ) {
                      return Promise.resolve()
                    }
                    if (value > min) {
                      return Promise.resolve()
                    }
                    return Promise.reject(
                      new Error(
                        'Max. players must be greater than Min. players',
                      ),
                    )
                  },
                }),
              ]}
            >
              <Input
                type="number"
                min={0}
                placeholder="0"
                className="br-12"
                onWheel={e => e.currentTarget.blur()}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  )
}

export default AddTeamForm
